import {Component, HostListener, OnInit} from '@angular/core';
import {MachinesService} from '../../../services/machines.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Machine} from '../../../model/machine';
import {MachinePersistRequest} from '../../../model/requests/machine-persist-request';
import {TreeNode} from 'primeng/api';
import {MapService} from '../../../services/map.service';
import * as mapboxgl from 'mapbox-gl';
import {ComponentCanDeactivate} from '../../../common/can-deactivate/component-can-deactivate';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {AuthGuard} from "../../../common/auth-guard.service";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {MachineModel} from "../../../model/machine-model";
import {MachineModelsService} from "../../../services/machine-models.service";
import {HierarchyService} from "../../../services/hierarchy.service";
import {TranslocoService} from "@ngneat/transloco";
import {dateRange, latitude, longitude, uppercase} from "../../../common/zmx-validators";
import {GeocodingService} from "../../../services/geocoding.service";
import {Geocoding} from "../../../model/geocoding";
import {MachineGroupsService} from "../../../services/machine-groups.service";
import {MachineGroup} from "../../../model/machine-group";
import {noSpecialCharacters, noWhitespaces} from "../../../shared/custom-validators";
import {AppConfig} from "../../../shared/global-variables";
import {TimezoneService} from "../../../services/timezone.service";

@Component({
  selector: 'app-machines-form',
  templateUrl: './machines-form.component.html',
  styleUrls: ['./machines-form.component.scss']
})
export class MachinesFormComponent implements OnInit, ComponentCanDeactivate {

  public readonly DEFAULT_ZOOM: number = 10;
  public readonly DEFAULT_ZOOM_CREATE: number = 5;
  public readonly DEFAULT_LATITUDE: number = 39.58278;
  public readonly DEFAULT_LONGITUDE: number = -0.38708;

  public mode: 'create' | 'edit';

  public hierarchy: TreeNode[];
  public models: MachineModel[];
  public groups: any[];
  public organization: any;

  public selectedModel: MachineModel;
  public selectedGroups: any[];

  public machineForm: FormGroup;
  public isMachineLoading: boolean = true;
  public persistingChanges: boolean;
  public editMachineFromGroup: boolean;

  public availableYearRange: string;
  public startDate: Date;
  public endLastSubscription: Date;

  private readonly LATITUDE: string = 'latitude';
  private readonly LONGITUDE: string = 'longitude';
  private readonly START_DATE: string = 'startDate';
  private readonly END_DATE: string = 'endDate';
  private readonly COUNTRY: string = 'country';
  private readonly STATE: string = 'state';
  private readonly CREATE = 'create';
  private readonly EDIT = 'edit';
  private readonly PERMISSION_MANAGE_ADVANCED_MACHINES: string = 'PERMISSION_MANAGE_ADVANCED_MACHINES';

  private map: mapboxgl.Map;
  private marker: mapboxgl.Marker;

  private language: string;
  private nodes: any[];
  private oldOrganizationUuid: string;

  private machine: Machine;
  private formSuccess: boolean;


  constructor(private machineModelsService: MachineModelsService,
              private machineGroupsService: MachineGroupsService,
              private hierarchyService: HierarchyService,
              private formBuilder: FormBuilder,
              private machinesService: MachinesService,
              private geocodingService: GeocodingService,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private commonMessagesService: CommonMessagesService,
              private readonly timezoneService: TimezoneService,
              private mapService: MapService,
              public authGuard: AuthGuard) {
    this.availableYearRange = this.timezoneService.calcYearRange();
    this.selectedGroups = [];
    this.editMachineFromGroup = false;
  }

  ngOnInit(): void {
    this.language = this.i18n.getActiveLang();
    this.loadDataAndForm();
  }

  private loadDataAndForm(): void {
    this.isMachineLoading = true;
    const promises = [
      this.machineModelsService.getModels().toPromise().then(response => {
        this.models = response.models;
      })
    ];
    if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ADVANCED_MACHINES)) {
      promises.push(
        this.machineGroupsService.getGroupsSummary().toPromise().then(response => {
          this.groups = [];
          for (const group of response.machineGroups) {
            this.groups.push({label: group.name, value: group.machineGroupUuid});
          }
        })
      );
    }

    Promise.all(promises).then(() => {
      this.route.url.subscribe(urlSegment => {
        const firstPath = urlSegment[0].path;
        if (firstPath === 'new') {
          this.mode = this.CREATE;
          this.loadCreationForm();
        } else {
          this.mode = this.EDIT;
          this.loadEditionForm(firstPath);
        }
      });
    }).finally(() => this.isMachineLoading = false
    );
  }

  private loadCreationForm(): void {
    this.machine = new Machine();
    this.machine.automaticGeoloc = true;
    this.createForm(this.machine);
  }

  private loadEditionForm(machineUuid: string): void {
    this.machinesService.getMachine(machineUuid).toPromise()
      .then(response => {
        this.machine = response.machine;
        this.oldOrganizationUuid = this.machine.organization.organizationUuid;
        this.createForm(this.machine);
        this.machineForm.addControl('uuid', new FormControl(this.machine.machineUuid,
          [Validators.required, Validators.maxLength(36)]));

        let subscriptionHistory = this.machine.subscriptionHistory;
        if (subscriptionHistory && subscriptionHistory.length > 0) {
          subscriptionHistory.forEach(subscription => {
            let endDate = new Date(subscription.endDate);
            if (this.endLastSubscription == null || endDate > this.endLastSubscription) {
              this.endLastSubscription = endDate;
            }
          });
        }

      }, error => {
        if (error.status == 404) {
          this.router.navigate(['/not-found']);
        }
      });
  }

  private createForm(machine: Machine): void {
    this.machineForm = this.formBuilder.group({
      alias: [machine.alias, [Validators.maxLength(255), Validators.required]],
      description: [machine.description, [Validators.maxLength(255)]],
      simNumber: [machine.simNumber, [Validators.maxLength(25)]],
      serial: [machine.serial, [Validators.maxLength(255), Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.required, noSpecialCharacters, noWhitespaces]],
      latitude: [machine.latitude, [Validators.required, latitude()]],
      longitude: [machine.longitude, [Validators.required, longitude()]],
      model: [machine.model, [Validators.required]],
      active: [(machine.subscription?.active != undefined) ? machine.subscription.active : false, [Validators.required]],
      automaticGeoloc: [machine.automaticGeoloc, [Validators.required]],
      startDate: [machine.subscription?.startDate ? new Date(machine.subscription.startDate) : null],
      endDate: [machine.subscription?.endDate ? new Date(machine.subscription.endDate) : null],
      eui: [machine.telemetryData?.integrationIdentifier, [Validators.required, Validators.minLength(16),
        Validators.maxLength(16), uppercase()]],
      locality: [{
        value: machine.geocoding?.locality[this.language],
        disabled: true
      }],
      state: [{
        value: machine.geocoding?.state[this.language],
        disabled: true
      }],
      country: [{
        value: machine.geocoding?.country[this.language],
        disabled: true
      }],
      machineUuid: this.mode === this.EDIT ? this.machine.machineUuid : null
    }, {
      validators: dateRange()
    });
    if (this.mode === this.CREATE || !machine.subscription?.active) {
      this.machineForm.get(this.START_DATE).disable();
      this.machineForm.get(this.END_DATE).disable();
    }
    this.selectedModel = machine.model;
    if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ADVANCED_MACHINES)
      && machine.groups != null && machine.groups.length > 0) {
      this.selectGroups(machine.groups);
    }
    this.buildTreeNode();
    this.machineForm.markAsPending();
    this.buildAndConfigureMap();
  }

  private selectGroups(groups: MachineGroup[]): void {
    for (const group of groups) {
      this.selectedGroups.push({label: group.name, value: group.machineGroupUuid});
    }
  }

  private buildAndConfigureMap(): void {
    const latitude = this.machine.latitude ? this.machine.latitude : this.DEFAULT_LATITUDE;
    const longitude = this.machine.longitude ? this.machine.longitude : this.DEFAULT_LONGITUDE;

    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, latitude, longitude, 'map');
    this.setMachineLocation(latitude, longitude);
    this.mapService.buildGeocoder(this.map);

    this.map.on('click', (e) => {
      this.setMachineLocation(e.lngLat.lat, e.lngLat.lng);
    });

    this.marker = this.mapService.buildStaticPulsingDot(this.map, this.machineLocation[1], this.machineLocation[0]);
  }

  setMachineLocation(latitude: number, longitude: number): void {
    this.machineForm.get(this.LATITUDE).setValue(latitude);
    this.machineForm.get(this.LONGITUDE).setValue(longitude);
    this.geocodingService.getGeocoding(longitude, latitude).subscribe(response => {
      let geocoding: Geocoding = response.geocoding;
      this.machineForm.get(this.COUNTRY).setValue(geocoding.country[this.language]);
      this.machineForm.get(this.STATE).setValue(geocoding.state[this.language]);
      this.machineForm.get('locality').setValue(geocoding.locality[this.language]);
    });
  }

  changeSerialNumber(): void {
    this.machineForm?.get('alias').setValue(this.machineForm?.get('serial').value);
  }

  formatEui(euiRaw: string): void {
    let formattedEui: string = euiRaw?.replace(/ /g, "");
    this.machineForm?.get('eui').setValue(formattedEui);
  }

  handleChangeEui(): void {
    this.formatEui(this.machineForm?.get('eui').value);
  }

  handlePasteEui(event: any): void {
    event.stopPropagation();
    event.preventDefault();

    const clipboardData = event.clipboardData;
    const pastedData = clipboardData.getData('Text');

    this.formatEui(pastedData);
  }

  // --- MAP ANIMATION --- //

  onLocationInputChange(): void {
    this.marker?.setLngLat(this.machineLocation);
  }

  get machineLocation(): string[] {
    return [this.machineForm.get(this.LONGITUDE).value, this.machineForm.get(this.LATITUDE).value];
  }

  private buildTreeNode(): void {
    this.hierarchyService.getHierarchyDropdown().subscribe(response => {
      let responseTreeNode = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto);
      this.nodes = responseTreeNode[0];
      this.hierarchy = responseTreeNode[1];
      if (this.mode === this.EDIT) {
        this.organization = this.nodes[this.machine.organization.organizationUuid];
        if (this.organization == null) {
          this.editMachineFromGroup = true;
          this.hierarchy = [];
          this.organization = {
            label: this.machine.organization.name + ' (' + this.machine.organization.organizationType.name + ')',
            data: this.machine.organization.organizationUuid
          };
        }
      } else {
        this.organization = {
          label: this.hierarchy[0]?.label,
          data: this.hierarchy[0]?.data
        };
      }
    });
  }

  onSubscriptionStatusChange(event): void {
    if (event.value) {
      this.changeDateState(this.START_DATE);
    }
  }

  changeDateState(dateField: string): void {
    if (this.machineForm.get(dateField)?.disabled) {
      this.machineForm.get(dateField).enable();
    }
    this.startDate = this.machineForm.get(this.START_DATE).value;
    this.machineForm.get(dateField).setValue(null);
  }

  setStatusActive(): void {
    this.machineForm.get(this.START_DATE).enable();
    this.machineForm.get(this.END_DATE).enable();
  }

  setStatusInactive(): void {
    this.clearDate(this.START_DATE);
    this.clearDate(this.END_DATE);
  }

  clearDate(dateField: string): void {
    this.machineForm.get(dateField).setValue(null);
    this.machineForm.get(dateField).disable();
  }

  isActiveSubscription(): boolean {
    return this.machineForm.get('active').value;
  }

  persistMachineData(): void {
    if (this.mode === this.EDIT && this.oldOrganizationUuid != this.organization.data) {
      this.commonMessagesService.showMoveMachineConfirmation(() => {
        this.save();
      });
    } else {
      this.save();
    }
  }

  private save(): void {
    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === this.CREATE) {
      promise = this.machinesService.createMachine(persistData).toPromise();
    } else {
      promise = this.machinesService.editMachine(persistData).toPromise();
    }

    promise.then((response) => {
      if (this.mode === this.CREATE) {
        this.commonMessagesService.showSuccessfullyCreatedMessage();
      } else {
        this.commonMessagesService.showSuccessfullyUpdatedMessage();
      }
      this.formSuccess = true;
      this.router.navigate([`/machines/${response.body.uuid}/details`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, error.error?.uuid.length === 16 ? error.error.uuid : persistData.serial);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): MachinePersistRequest {
    const formValue = this.machineForm.getRawValue();
    let periodRange = {from: null, until: null};
    if (formValue.startDate && formValue.endDate) {
      periodRange = this.timezoneService.timestampRangeFromCalendarData([formValue.startDate, formValue.endDate]);
    }
    return {
      alias: formValue.alias?.trim(),
      description: formValue.description?.trim(),
      serial: formValue.serial?.trim(),
      latitude: formValue.latitude,
      longitude: formValue.longitude,
      organizationUuid: this.organization.data,
      machineModelUuid: formValue.model.machineModelUuid,
      active: formValue.active,
      automaticGeoloc: formValue.automaticGeoloc,
      startDate: periodRange.from,
      endDate: periodRange.until,
      machineGroupsUuids: this.authGuard.hasPermission(this.PERMISSION_MANAGE_ADVANCED_MACHINES) ? this.getSelectedGroupsUuids() : [],
      eui: formValue.eui?.trim(),
      machineUuid: this.mode === this.EDIT ? this.machine.machineUuid : null,
      simNumber: formValue.simNumber?.trim()
    };
  }

  private getSelectedGroupsUuids(): string[] {
    return this.selectedGroups ? this.selectedGroups.map(group => group.value) : [];
  }

  isValid(): boolean {
    if (this.machineForm?.get('active').value &&
      (!this.machineForm?.get(this.START_DATE).value || !this.machineForm?.get(this.END_DATE).value)) {
      return false;
    }
    return this.machineForm?.valid && this.organization && this.machineForm.get(this.COUNTRY).value
      && this.machineForm.get(this.STATE).value;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || (this.machineForm.untouched && this.groupsUntouched() && this.organizationUntouched()
      && this.machineForm.get(this.LATITUDE).value == this.machine.latitude
      && this.machineForm.get(this.LONGITUDE).value == this.machine.longitude);
  }

  private groupsUntouched(): boolean {
    if (this.mode === this.CREATE) {
      return this.selectedGroups.length === 0;
    } else {
      let newMachineGroupsUuids: string[] = this.getSelectedGroupsUuids().sort((a, b) => a.localeCompare(b));
      let oldMachineGroupsUuids: string[] = this.machine?.groups ?
        this.machine.groups.map(group => group.machineGroupUuid).sort((a, b) => a.localeCompare(b)) : [];
      return JSON.stringify([oldMachineGroupsUuids]) === JSON.stringify([newMachineGroupsUuids]);
    }
  }

  private organizationUntouched(): boolean {
    if (this.mode === this.CREATE) {
      return this.organization == null;
    } else {
      return this.organization.data === this.machine.organization.organizationUuid;
    }
  }

}
